import { ChangeDetectorRef, Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { AvanoLoadingIndicatorService } from './avano-loading-indicator.service';

/**
 * Directive to render a blocked state of a Ui element identified by the given selector.<br>
 * This directive is intended to be used in combination with the {@link AvanoLoadingIndicatorService}.<br>
 * <br>
 * Usage: &lt;div [avanoLoadingIndicator]="'name'"&gt;
 * UI/UX-Specification: https://confluence.rt.ipnt.de/display/UIX/%5BAVANO+Styleguide%5D+Loading+Indicator
 */
@Directive({
  selector: '[avanoLoadingIndicator]'
})
export class AvanoLoadingIndicatorDirective implements OnInit, OnDestroy {
  /** A unique name to identify the Ui element which is about to be blocked. */
  @Input('avanoLoadingIndicator') avanoLoadingIndicator: string;

  /** Css class for the actual block element. */
  private loadingIndicatorStyleClass = 'avano-loading-indicator';

  /** Css class for the parent element to define scope. */
  private blockedParentStyleClass = 'avano-loading-indicator-parent';
  private loadingIndicatorOverlayClass = 'avano-loading-indicator-overlay';
  private loadingIndicatorSpinnerElement = 'avano-loading-spinner-element';
  private loadingIndicatorSpinnerContainer = 'avano-loading-spinner-container';

  /** Root element to work on. */
  private rootElement: HTMLElement;

  /** Actual blocker element. */
  private blockEl: HTMLElement;

  /**
   * Constructor for the {@link AvanoLoadingIndicatorDirective}.
   *
   * @param {ElementRef} rootElement - The injected rootElement.
   * @param {AvanoLoadingIndicatorService} avanoLoadingIndicatorService - The injected avanoLoadingIndicatorService.
   * @param {ChangeDetectorRef} changeDetectionRef - The injected changeDetectionRef.
   */
  constructor(rootElement: ElementRef, private avanoLoadingIndicatorService: AvanoLoadingIndicatorService, private changeDetectionRef: ChangeDetectorRef) {
    this.rootElement = rootElement.nativeElement;
  }

  /**
   *  Called when initializing.<br>
   *  Registers the directive on the {@link AvanoLoadingIndicatorService}.
   */
  ngOnInit(): void {
    this.avanoLoadingIndicatorService.register(this);
  }

  /**
   * Renders the blocked state of a Ui element.<br>
   * <i>Intended to be called by {@link AvanoLoadingIndicatorService}.</i>
   */
  block(): void {
    if (!this.blockEl) {
      this.rootElement.classList.add(this.blockedParentStyleClass);

      // wrapper element
      this.blockEl = document.createElement('div');
      this.blockEl.className = this.loadingIndicatorStyleClass;

      // spinner container element
      const spinnerContainer: HTMLElement = document.createElement('div');
      spinnerContainer.className = this.loadingIndicatorSpinnerContainer;
      const spinner: HTMLElement = document.createElement('div');
      // actual spinner element
      spinner.className = this.loadingIndicatorSpinnerElement;
      spinnerContainer.appendChild(spinner);
      this.blockEl.appendChild(spinnerContainer);

      // overlay element
      const overlay: HTMLElement = document.createElement('div');
      overlay.className = this.loadingIndicatorOverlayClass;
      this.blockEl.appendChild(overlay);

      this.rootElement.insertBefore(this.blockEl, this.rootElement.firstChild);
      this.changeDetectionRef.detectChanges();
    }
  }

  /**
   * Renders the 'unblocked' state of a Ui element.<br>
   * <i>Intended to be called by {@link AvanoLoadingIndicatorService}.</i>
   */
  unblock(): void {
    if (this.blockEl) {
      this.rootElement.classList.remove(this.blockedParentStyleClass);
      this.rootElement.removeChild(this.blockEl);
      this.blockEl = null;
      this.changeDetectionRef.detectChanges();
    }
  }

  /**
   *  Called when destroying.<br>
   *  Deregisters the directive on the {@link AvanoLoadingIndicatorService}.
   */
  ngOnDestroy(): void {
    this.avanoLoadingIndicatorService.deregister(this);
  }
}
